import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdContentCopy, MdCheck } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { editContent } from '../../api/contentApi';
import { updateMediaCaptionRequest, resetMediaCaptionState } from '../../redux/slices/mediaCaptionSlice';
import type { RootState } from '../../redux/store';
import type { SocialMediaContent } from './types';

interface EditCaptionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  captions: Record<string, SocialMediaContent>;
  contentIds: Record<string, string>; // Map platform to content_id
  restaurantId: string;
  campaignId?: string; // Optional campaign ID for new API
  onSave: (captions: Record<string, SocialMediaContent>) => void;
}

// Helper function to format platform names for display
const formatPlatformName = (platform: string): string => {
  const platformMap: Record<string, string> = {
    'Facebook': 'Facebook',
    'Instagram': 'Instagram',
    'Twitter_x': 'X (Twitter)',
    'Twitter': 'Twitter',
    'Whatsapp': 'WhatsApp',
    'Tiktok': 'TikTok',
    'Email': 'Email',
  };
  
  return platformMap[platform] || platform;
};

const EditCaptionsPanel: React.FC<EditCaptionsPanelProps> = ({
  isOpen,
  onClose,
  captions,
  contentIds,
  restaurantId,
  campaignId,
  onSave
}) => {
  const dispatch = useDispatch();
  
  const [editedCaptions, setEditedCaptions] = useState<Record<string, SocialMediaContent>>(captions);
  const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
  const [savingPlatform, setSavingPlatform] = useState<string | null>(null); // Track which platform is being saved

  // Update local state when captions prop changes
  useEffect(() => {
    setEditedCaptions(captions);
  }, [captions]);

  const handleCopy = async (platform: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPlatform(platform);
      setTimeout(() => setCopiedPlatform(null), 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSave = () => {
    onSave(editedCaptions);
    onClose();
  };

  const handleSavePlatform = async (platform: string) => {
    // Set button to "Saving..." state
    setSavingPlatform(platform);
    
    try {
      // Use new media caption API via Redux if campaignId is available
      if (campaignId) {
        dispatch(updateMediaCaptionRequest({
          campaignId,
          restaurantId,
          platform,
          postCopy: editedCaptions[platform].post_copy
        }));
        
        // Wait a bit for the saga to complete
        setTimeout(() => {
          // Update parent component
          onSave({
            ...captions,
            [platform]: editedCaptions[platform]
          });
          
          // Set button to "Saved" state
          setSavingPlatform(`${platform}-saved`);
          
          // Reset button to "Save" after 1 second
          setTimeout(() => {
            setSavingPlatform(null);
          }, 1000);
        }, 500);
      } else {
        // Fall back to old API for legacy flows
        const contentId = contentIds[platform];
        if (!contentId) {
          console.error('No content ID found for platform:', platform);
          setSavingPlatform(null);
          return;
        }
        
        // For Email, include subject_line in the payload
        const payload: any = {
          post_copy: editedCaptions[platform].post_copy,
          visual_brief: editedCaptions[platform].visual_brief
        };
        
        if (platform === 'Email' && editedCaptions[platform].subject_line) {
          payload.subject_line = editedCaptions[platform].subject_line;
        }
        
        // Make API call
        await editContent(contentId, restaurantId, payload);
        
        // Update parent component
        onSave({
          ...captions,
          [platform]: editedCaptions[platform]
        });
        
        // Set button to "Saved" state
        setSavingPlatform(`${platform}-saved`);
        
        // Reset button to "Save" after 1 second
        setTimeout(() => {
          setSavingPlatform(null);
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to save caption:', error);
      setSavingPlatform(null);
    }
  };

  const handleFieldChange = (platform: string, field: keyof SocialMediaContent, value: string) => {
    setEditedCaptions(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  const handleVisualBriefChange = (
    platform: string,
    field: 'image_prompt' | 'image_style' | 'aspect_ratio' | 'video_idea',
    value: string
  ) => {
    setEditedCaptions(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        visual_brief: {
          ...(prev[platform].visual_brief || { image_prompt: '', image_style: '', aspect_ratio: '', video_idea: '' }),
          [field]: value
        }
      }
    }));
  };

  // Get array of platform keys from captions
  const platforms = Object.keys(editedCaptions);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40"
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md z-50  flex flex-col"
            style={{
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 0 14px 0 #0000001a',
                  background: 'linear-gradient(90deg, #212121eb, #373737eb)',
                  border: '1px solid #e0e0e021'
                }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-brand-primary">Edit Media Captions</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
              >
                <MdClose size={24} className="text-white cursor-pointer" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {platforms.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  No social media captions available
                </div>
              ) : (
                platforms.map((platform) => {
                  const content = editedCaptions[platform];
                  return (
                    <div key={platform} className="space-y-4 pb-6 border-b border-gray-700 last:border-b-0">
                      {/* Platform Header */}
                      <h3 className="text-lg font-semibold text-white mb-1">{formatPlatformName(platform)}</h3>
                      
                      {/* Subject Line (for Email) */}
                      {platform === 'Email' && (
                        <div>
                          <label className="block text-gray-400 text-xs font-medium mb-2">
                            Subject Line
                          </label>
                          <input
                            type="text"
                            value={content.subject_line || ''}
                            onChange={(e) => handleFieldChange(platform, 'subject_line', e.target.value)}
                            placeholder="Enter email subject line..."
                            className="w-full px-4 py-2 bg-surface-dark text-sm border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary transition-colors"
                          />
                        </div>
                      )}
                      
                      {/* Post Copy */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-gray-400 text-xs font-medium">
                            Post Copy
                          </label>
                          <div className="flex items-center gap-2">
                            {/* Copy Button */}
                            <button
                              onClick={() => handleCopy(platform, content.post_copy || '')}
                              className="p-1.5 bg-gray-700 hover:bg-gray-600 cursor-pointer rounded-md transition-colors"
                              title="Copy to clipboard"
                            >
                              {copiedPlatform === platform ? (
                                <MdCheck size={14} className="text-green-400" />
                              ) : (
                                <MdContentCopy size={14} className="text-gray-300" />
                              )}
                            </button>
                            {/* Save Button with states */}
                            <button
                              onClick={() => handleSavePlatform(platform)}
                              disabled={savingPlatform === platform}
                              className={`px-3 py-1.5 text-white text-xs font-medium cursor-pointer rounded-md transition-colors ${
                                savingPlatform === `${platform}-saved`
                                  ? 'bg-green-600'
                                  : savingPlatform === platform
                                  ? 'bg-gray-600 cursor-not-allowed'
                                  : 'bg-brand-primary hover:bg-[#d63d20]'
                              }`}
                            >
                              {savingPlatform === `${platform}-saved`
                                ? 'Saved'
                                : savingPlatform === platform
                                ? 'Saving...'
                                : 'Save'}
                            </button>
                          </div>
                        </div>
                        <textarea
                          value={content.post_copy || ''}
                          onChange={(e) => handleFieldChange(platform, 'post_copy', e.target.value)}
                          placeholder={`Enter ${formatPlatformName(platform)} caption...`}
                          rows={8}
                          className="w-full px-4 py-3 bg-surface-dark border border-gray-700 text-sm rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary transition-colors resize-none"
                        />
                      </div>

                      {/* Visual Brief Section - Informational Card */}
                      {/* {content.visual_brief && (
                        <div className="mt-2 pt- b border-gray-600">
                          <h4 className="text-xs font-normal text-[#fff] mb-2">Visual Brief</h4>
                          
                          <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-3 space-y-2">
                            {content.visual_brief.image_prompt && (
                              <div>
                                <span className="text-gray-400 font-medium" style={{ fontSize: '12px' }}>Image Prompt: </span>
                                <span className="text-gray-300" style={{ fontSize: '10px' }}>{content.visual_brief.image_prompt}</span>
                              </div>
                            )}
                            
                            {content.visual_brief.image_style && (
                              <div>
                                <span className="text-gray-400 font-medium" style={{ fontSize: '12px' }}>Image Style: </span>
                                <span className="text-gray-300" style={{ fontSize: '10px' }}>{content.visual_brief.image_style}</span>
                              </div>
                            )}
                            
                            {content.visual_brief.aspect_ratio && (
                              <div>
                                <span className="text-gray-400 font-medium" style={{ fontSize: '12px' }}>Aspect Ratio: </span>
                                <span className="text-gray-300" style={{ fontSize: '10px' }}>{content.visual_brief.aspect_ratio}</span>
                              </div>
                            )}
                            
                            {content.visual_brief.video_idea && (
                              <div>
                                <span className="text-gray-400 font-medium" style={{ fontSize: '12px' }}>Video Idea: </span>
                                <span className="text-gray-300" style={{ fontSize: '10px' }}>{content.visual_brief.video_idea}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )} */}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditCaptionsPanel;
